import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { Search, X } from 'lucide-react'
import LogoGradient from '../brand/assets/logo-gradient.png'
import type { IndexGroupScope, IndexHomeData, IndexResults, IndexScope } from './types'
import { IndexHome } from './IndexHome'
import { IndexResults as Results } from './IndexResults'
import { SCOPE_TABS, buildView, homeOrder, scopeTotals } from './view'
import styles from './IndexSearch.module.css'

export interface IndexSearchProps {
  open: boolean
  onClose: () => void
  query: string
  onQueryChange: (query: string) => void
  scope: IndexScope
  onScopeChange: (scope: IndexScope) => void
  /** The latest response for `query`; null until one arrives. */
  results: IndexResults | null
  home: IndexHomeData
  /** A request is in flight. Previous results stay up meanwhile. */
  loading?: boolean
  /** Shown in place of results — pass the API's own message. */
  error?: string | null
  /** Route to a result. The Index closes itself afterwards. */
  onNavigate: (href: string) => void
  /** Defaults to re-running the recent search. */
  onRecentSelect?: (query: string) => void
  onClearRecent?: () => void
  placeholder?: string
}

/**
 * The keyword as the API echoes it back: lowercased, single-spaced, capped at
 * 64 characters — so the Index can tell whether results are for what's typed.
 */
function normalize(query: string): string {
  const spaced = query.trim().toLowerCase().split(/\s+/).join(' ')
  return [...spaced].slice(0, 64).join('').trim()
}

/**
 * The Carousel Index: full-screen search across players, teams, clubs,
 * leagues and competitions. Presentational — the app fetches and routes.
 */
export function IndexSearch({
  open,
  onClose,
  query,
  onQueryChange,
  scope,
  onScopeChange,
  results,
  home,
  loading = false,
  error = null,
  onNavigate,
  onRecentSelect,
  onClearRecent,
  placeholder = 'Search players, teams, leagues…',
}: IndexSearchProps) {
  const idPrefix = `index${useId().replace(/:/g, '')}`
  const inputRef = useRef<HTMLInputElement>(null)
  const typed = query.trim().length > 0
  const view = useMemo(() => buildView(typed ? results : null, scope), [typed, results, scope])
  const order = useMemo(() => (view.layout === 'home' ? homeOrder(home) : view.order), [view, home])
  // Tab counts come from the "All" search: a single tab only knows its own
  // total, and the other tabs shouldn't go blank while one is open.
  const [allTotals, setAllTotals] = useState<{ query: string; totals: ReturnType<typeof scopeTotals> }>({ query: '', totals: {} })
  if (typed && results && scope === 'all' && allTotals.query !== results.query) {
    setAllTotals({ query: results.query, totals: scopeTotals(results) })
  }
  const totals = useMemo(() => {
    if (!typed || !results) return {}
    if (scope !== 'all' && allTotals.query === results.query) return allTotals.totals
    return scopeTotals(results)
  }, [typed, results, scope, allTotals])
  // Results start with the first one highlighted, so Enter opens the best
  // match straight away; home starts with nothing, so typing isn't hijacked.
  const firstKey = order[0]?.key ?? null
  const startKey = view.layout === 'home' ? null : firstKey
  const [activeKey, setActiveKey] = useState<string | null>(startKey)

  // A new result set starts the highlight over.
  const [lastFirst, setLastFirst] = useState(firstKey)
  if (firstKey !== lastFirst) {
    setLastFirst(firstKey)
    setActiveKey(startKey)
  }

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    requestAnimationFrame(() => inputRef.current?.focus())
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  // Enter pressed before the answer for what's typed arrived: open its best
  // result as soon as it lands. Typing on drops it — the text no longer matches.
  const pendingEnter = useRef<string | null>(null)
  const typedKey = normalize(query)
  const fresh = results != null && results.query === typedKey
  useEffect(() => {
    if (!fresh || pendingEnter.current !== typedKey) return
    pendingEnter.current = null
    const best = order[0]
    if (best) {
      onNavigate(best.item.href)
      onClose()
    }
  }, [fresh, typedKey, order, onNavigate, onClose])

  useEffect(() => {
    if (activeKey) document.getElementById(`${idPrefix}-${activeKey}`)?.scrollIntoView({ block: 'nearest' })
  }, [activeKey, idPrefix])

  if (!open) return null

  const navigate = (href: string) => {
    onNavigate(href)
    onClose()
  }

  function onInputKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (order.length === 0) return
      e.preventDefault()
      const at = order.findIndex((o) => o.key === activeKey)
      const next = e.key === 'ArrowDown'
        ? (at + 1) % order.length
        : (at <= 0 ? order.length : at) - 1
      setActiveKey(order[next].key)
    } else if (e.key === 'Enter') {
      if (typed && !fresh) {
        e.preventDefault()
        pendingEnter.current = typedKey
        return
      }
      const hit = order.find((o) => o.key === activeKey)
      if (hit) {
        e.preventDefault()
        navigate(hit.item.href)
      }
    }
  }

  const resultsId = `${idPrefix}-results`
  // Announced to screen readers: how many this tab holds.
  const summary = typed && results
    ? view.layout === 'empty' ? 'No results' : `${results.groups.reduce((n, g) => n + g.total, 0)} results`
    : ''

  return createPortal(
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Carousel Index">
      <div className={styles.aura} aria-hidden="true" />
      <header className={styles.header}>
        <div className={styles.brand}>
          <img src={LogoGradient} alt="" className={styles.logo} />
          <span className={styles.wordmark}>
            Carousel <span className={styles.gradientText}>Index</span>
          </span>
          <span className={styles.tagline}>Find anyone, any team, any league</span>
        </div>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close search">
          <span className={styles.closeText}>Close</span>
          <kbd className={styles.kbd}>Esc</kbd>
          <X size={18} aria-hidden="true" />
        </button>
      </header>

      <div className={styles.searchArea}>
        <div className={styles.inputWrap}>
          <Search size={22} className={styles.inputIcon} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-label="Search Carousel"
            aria-expanded={order.length > 0}
            aria-controls={resultsId}
            aria-autocomplete="list"
            aria-activedescendant={activeKey ? `${idPrefix}-${activeKey}` : undefined}
            className={styles.input}
            placeholder={placeholder}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={onInputKey}
            autoComplete="off"
            spellCheck={false}
          />
          {loading && <span className={styles.searching}>Searching…</span>}
          <span className={styles.srOnly} role="status">{summary}</span>
        </div>
        <nav aria-label="Search scope" className={styles.tabs}>
          {SCOPE_TABS.map((t) => {
            const count = totals[t.scope]
            return (
              <button
                key={t.scope}
                type="button"
                className={styles.tab}
                aria-pressed={scope === t.scope}
                data-empty={count === 0 || undefined}
                onClick={() => {
                  onScopeChange(t.scope)
                  // A tab narrows the search box; keep typing and Enter there.
                  inputRef.current?.focus()
                }}
              >
                {t.label}
                {count != null && <span className={styles.tabCount}>{count}</span>}
              </button>
            )
          })}
        </nav>
      </div>

      <main id={resultsId} className={styles.body}>
        {error ? (
          <div className={styles.empty} role="alert">
            <p className={styles.emptyTitle}>Search isn’t working right now</p>
            <p className={styles.rowSub}>{error}</p>
          </div>
        ) : typed && !results ? (
          <div className={styles.empty}>
            <p className={styles.rowSub}>Searching…</p>
          </div>
        ) : view.layout === 'home' ? (
          <IndexHome
            home={home}
            idPrefix={idPrefix}
            activeKey={activeKey}
            onNavigate={navigate}
            onHover={setActiveKey}
            onRecentSelect={onRecentSelect ?? onQueryChange}
            onClearRecent={onClearRecent}
          />
        ) : (
          <Results
            view={view}
            query={query}
            activeKey={activeKey}
            idPrefix={idPrefix}
            onNavigate={navigate}
            onHover={setActiveKey}
            onSeeAll={(s: IndexGroupScope) => {
              onScopeChange(s)
              inputRef.current?.focus()
            }}
          />
        )}
      </main>

      <footer className={styles.footer} aria-hidden="true">
        <span><kbd className={styles.kbd}>↑</kbd><kbd className={styles.kbd}>↓</kbd> Move</span>
        <span><kbd className={styles.kbd}>↵</kbd> Open</span>
        <span><kbd className={styles.kbd}>Esc</kbd> Close</span>
      </footer>
    </div>,
    document.body,
  )
}
